import { useI18n } from '@whizz/react-i18n';
import React, { useEffect, useState } from 'react';

type PasswordStrengthIndicatorProps = {
  password: string;
};

type StrengthMeta = {
  label: string;
  trackClass: string;
  barClass: string;
};

const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps): JSX.Element => {

  const { translate } = useI18n();
  const [ strength, setStrength ] = useState<number>(0);

  useEffect(() => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);
    const words = password.trim().split(/\s+/).filter((word) => word.length >= 3);
    const uniqueWords = new Set(words.map((word) => word.toLowerCase()));
    const isPassphraseStrong = words.length >= 3 && uniqueWords.size >= 3;
    const canBeStrong = (hasUppercase && hasNumber && hasSymbol) || isPassphraseStrong;

    import('zxcvbn').then((zxcvbn) => {
      const baseStrength = (zxcvbn.default(password).score + 1) / 5 * 100;
      setStrength(canBeStrong ? baseStrength : Math.min(baseStrength, 80));
    });
  }, [ password ]);

  let meta: StrengthMeta;

  if (strength <= 40) {
    meta = {
      label: translate('components.PASSWORD_STRENGTH_INDICATOR.WEAK'),
      trackClass: 'bg-red-100',
      barClass: 'bg-red-500',
    };
  } else if (strength <= 80) {
    meta = {
      label: translate('components.PASSWORD_STRENGTH_INDICATOR.AVERAGE'),
      trackClass: 'bg-yellow-100',
      barClass: 'bg-yellow-500',
    };
  } else {
    meta = {
      label: translate('components.PASSWORD_STRENGTH_INDICATOR.STRONG'),
      trackClass: 'bg-green-100',
      barClass: 'bg-green-500',
    };
  }

  return (
    <div className="space-y-1">
      <div
        className={`h-2 w-full overflow-hidden rounded ${meta.trackClass}`}
        role="progressbar"
        aria-label={meta.label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(strength)}
      >
        <div
          className={`h-full rounded transition-all duration-300 ${meta.barClass}`}
          style={{ width: `${strength}%` }}
        />
      </div>
      <p className="text-xs text-muted">{meta.label}</p>
    </div>
  );

};

export default PasswordStrengthIndicator;
